/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { HtmlHeadInclude, IAny, IGeneric } from '@msdyn365-commerce/core';
import * as React from 'react';

import { IExtProductPageSummaryData } from './ext-product-page-summary.data';
import { IExtProductPageSummaryProps } from './ext-product-page-summary.props.autogenerated';

interface ICategoryBinding {
    bindings: ICategoryBindingItem[];
    default: string;
}

interface ICategoryBindingItem {
    google_id: string;
    categories: string[];
}
/**
 *
 * ProductPageSummary component.
 * @extends {React.PureComponent<IExtProductPageSummaryProps>}
 */
class ProductPageSummary extends React.PureComponent<IExtProductPageSummaryProps<IExtProductPageSummaryData>> {
    public render(): JSX.Element {
        if (this.props.data.productPageSummary.result === undefined) {
            throw new Error('PageSummaryData input to page-summary is undefined. Unable to generate page-summary');
        }
        let {
            data: {
                productPageSummary: {
                    result: { title, description, keywords, sharingImageUrl, faviconUrl }
                }
            }
        } = this.props;

        const {
            config,
            context,
            data: {
                productPageSummary: {
                    result: { canonicalUrl, productId, productBrand, price, category }
                }
            }
        } = this.props;

        // Product SEO metadata specific information - non-overridable
        const productName = title;
        const productDescription = description;
        const productImageUrl = sharingImageUrl;

        // Override values coming from data action if config values are provided
        title = config.title || title;
        description = config.description || description;
        keywords = config.keywords || keywords;
        sharingImageUrl = (config.sharingImage && config.sharingImage.src) || sharingImageUrl;
        faviconUrl = config.faviconUrl || faviconUrl;

        // Construct page title with suffix and prefix if provided from app settings
        const pageTitle = this._constructPageTitle(title, context && context.request && context.request.app);
        return (
            <>
                <HtmlHeadInclude>
                    {pageTitle && <title>{pageTitle}</title>}
                    {description && <meta name='description' content={description} />}
                    {keywords && <meta name='keywords' content={keywords} />}
                    {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
                    {faviconUrl && <link rel='shortcut icon' href={faviconUrl} />}
                </HtmlHeadInclude>
                {this._renderProductMetadata(
                    productName,
                    productDescription,
                    productImageUrl,
                    canonicalUrl,
                    productId,
                    price,
                    context.request.channel?.Currency,
                    productBrand,
                    category
                )}
                {!config.disableOgTags && this._renderOgTags(pageTitle, description, sharingImageUrl)}
                {!config.disableTwitterTags && this._renderTwitterTags(pageTitle, description, sharingImageUrl)}
            </>
        );
    }

    /**
     * Renders the facebook and other social media specific metadata tags.
     *
     * @param title Page title.
     * @param description Product description.
     * @param sharingImageUrl Primary product image url.
     */
    private _renderOgTags(title: string | undefined, description: string | undefined, sharingImageUrl: string | undefined): JSX.Element {
        return (
            <HtmlHeadInclude>
                {title && <meta property='og:title' content={title} />}
                {description && <meta property='og:description' content={description} />}
                {sharingImageUrl && <meta property='og:image' content={sharingImageUrl} />}
                <meta property='og:type' content='website' />
            </HtmlHeadInclude>
        );
    }

    /**
     * Constructs and renders the JSON-LD tag used to output product specific metadata used by search engine crawlers.
     *
     * @param productName Product name.
     * @param description Product description.
     * @param sharingImageUrl Primary product image url.
     * @param canonicalUrl Product canonical url.
     * @param productId Master product id.
     * @param masterProductId Master product id.
     * @param price Proudct price.
     * @param currencyCode Current channel currency code.
     * @param productBrand Product brand.
     * @param category Category.
     */
    private _renderProductMetadata(
        productName: string | undefined,
        description: string | undefined,
        sharingImageUrl: string | undefined,
        canonicalUrl: string | undefined,
        productId: number | undefined,
        price: number | undefined,
        currencyCode: string | undefined,
        productBrand: string | undefined,
        category?: string | undefined
    ): JSX.Element {
        var color = '';
        //Put the color in the query onto urls
        for (const key in this.props.context.request.query) {
            if (key === 'color') {
                /* eslint-disable-next-line security/detect-object-injection*/
                color = this.props.context.request.query[key];
                const strings = sharingImageUrl?.split('fileName=/');
                sharingImageUrl = strings ? strings[0] : '';
                /* eslint-disable-next-line security/detect-object-injection*/
                sharingImageUrl = `${sharingImageUrl}filename=/${productId}_${this.props.context.request.query[key]}_000_001.jpg`;
                /* eslint-disable-next-line security/detect-object-injection*/
                canonicalUrl = `${canonicalUrl}?color=${encodeURIComponent(this.props.context.request.query[key])}`;
            }
        }
        // Construct the JSON-LD data that contains the product metadata information used by search engine crawlers
        const productMetaData = JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'Product',
            name: productName,
            description,
            image: sharingImageUrl,
            productID: `${productId}${color}`,
            google_product_category: this._getCategoryId(category),
            brand: {
                '@type': 'Brand',
                name: productBrand
            },
            offers: {
                '@type': 'Offer',
                url: canonicalUrl,
                priceCurrency: currencyCode,
                price,
                itemCondition: 'https://schema.org/NewCondition',
                availability: 'https://schema.org/InStock'
            },
            additionalProperty: [
                {
                    '@type': 'PropertyValue',
                    propertyId: 'item_group_id',
                    value: productId
                }
            ]
        });
        return (
            <HtmlHeadInclude>
                {/* eslint-disable-next-line react/no-danger -- Implementation is a copy.*/}
                <script data-load-point='headEnd' type='application/ld+json' dangerouslySetInnerHTML={{ __html: productMetaData }} />
            </HtmlHeadInclude>
        );
    }

    /**
     * Gets the google category id for the category
     * @param category Category.
     */
    private _getCategoryId(category: string | undefined): string {
        const list: ICategoryBinding = JSON.parse(this.props.config.categoryBinding ?? '');
        let value = list.default;
        list.bindings.forEach((item: ICategoryBindingItem) => {
            item.categories.forEach(tmpCategory => {
                if (tmpCategory === category) {
                    value = item.google_id;
                }
            });
        });

        return value;
    }
    /**
     * Renders the twitter specific metadata tags.
     * @param title Page title.
     * @param description Product description.
     * @param sharingImageUrl Primary product image url.
     */
    private _renderTwitterTags(
        title: string | undefined,
        description: string | undefined,
        sharingImageUrl: string | undefined
    ): JSX.Element {
        return (
            <HtmlHeadInclude>
                {title && <meta name='twitter:title' content={title} />}
                {description && <meta name='twitter:description' content={description} />}
                {sharingImageUrl && <meta name='twitter:image' content={sharingImageUrl} />}
                <meta name='twitter:card' content='summary' />
            </HtmlHeadInclude>
        );
    }

    /**
     * Constructs the page title using the prefix and suffix as defined in app settings and the product name.
     * @param title The product name.
     * @param app App settings.
     */
    private _constructPageTitle(title: string | undefined, app: IGeneric<IAny>): string | undefined {
        if (!title) {
            title = '';
        }
        const moduleConfig = this.props.config;
        if (moduleConfig && moduleConfig.ignoreTitleSuffixPrefix) {
            return title;
        }
        const appConfig = app && app.platform;
        if (appConfig) {
            if (appConfig.pageTitlePrefix) {
                title = `${appConfig.pageTitlePrefix}${title}`;
            }
            if (appConfig.pageTitleSuffix) {
                title = `${title}${appConfig.pageTitleSuffix}`;
            }
        }

        return title;
    }
}

export default ProductPageSummary;
